import { useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, BadgeCheck, Clock, Users, CircleDollarSign, ExternalLink } from "lucide-react";

export default function AdminSalesReps() {
  const [tab, setTab] = useState("pending"); // pending | approved | all | commissions
  const [reps, setReps] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [comTotals, setComTotals] = useState({ all: 0, pending: 0, paid_out: 0 });
  const [loading, setLoading] = useState(true);

  const loadReps = async () => {
    setLoading(true);
    try {
      const params = tab === "all" || tab === "commissions" ? "" : `?status=${tab}`;
      const { data } = await api.get(`/admin/sales-reps${params}`);
      setReps(data.items || []);
    } catch {
      toast.error("Couldn't load sales reps");
    } finally {
      setLoading(false);
    }
  };

  const loadCommissions = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sales-reps/commissions");
      setCommissions(data.items || []);
      setComTotals(data.totals || { all: 0, pending: 0, paid_out: 0 });
    } catch {
      toast.error("Couldn't load commissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "commissions") loadCommissions();
    else loadReps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const approve = async (rep) => {
    try {
      await api.post(`/admin/sales-reps/${rep.id}/approve`);
      toast.success(`Approved — referral code generated`);
      loadReps();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't approve");
    }
  };

  const reject = async (rep) => {
    if (!window.confirm(`Reject ${rep.email}? They can re-apply later.`)) return;
    try {
      await api.post(`/admin/sales-reps/${rep.id}/reject`);
      toast.success("Application rejected");
      loadReps();
    } catch {
      toast.error("Couldn't reject");
    }
  };

  const toggleActive = async (rep) => {
    try {
      await api.patch(`/admin/sales-reps/${rep.id}`, { is_active: !rep.is_active });
      loadReps();
    } catch {
      toast.error("Couldn't update");
    }
  };

  const remove = async (rep) => {
    if (!window.confirm(`Delete ${rep.email}? Past commission history is retained for accounting.`)) return;
    try {
      await api.delete(`/admin/sales-reps/${rep.id}`);
      toast.success("Rep deleted");
      loadReps();
    } catch {
      toast.error("Couldn't delete");
    }
  };

  const markPaid = async (c) => {
    if (!window.confirm(`Mark ${fmtMoney(c.commission_amount)} commission for ${c.rep_email} as PAID OUT?`)) return;
    try {
      await api.post(`/admin/sales-reps/commissions/${c.id}/mark-paid`);
      toast.success("Marked as paid");
      loadCommissions();
    } catch {
      toast.error("Couldn't update");
    }
  };

  const pendingCount = useMemo(() => reps.filter((r) => r.rep_status === "pending").length, [reps]);

  return (
    <div className="space-y-6" data-testid="admin-sales-reps-page">
      <div>
        <div className="ipv-eyebrow flex items-center gap-2">
          <Users className="w-3 h-3" /> Referral program
        </div>
        <h1 className="font-display font-black text-3xl">Sales Reps</h1>
        <p className="text-sm text-gray-600 mt-1 max-w-2xl">
          Approved reps earn a flat <strong>10%</strong> commission on Furniture &amp; Scooters
          line items only. Pallets and other categories never earn commission.
        </p>
      </div>

      {/* Earnings overview */}
      <div className="grid grid-cols-3 gap-3" data-testid="rep-earnings-overview">
        <div className="border border-gray-200 p-4">
          <div className="text-[10px] tracking-widest uppercase text-gray-500 font-bold">Total commissions</div>
          <div className="font-display font-black text-2xl mt-1">{fmtMoney(comTotals.all)}</div>
        </div>
        <div className="border border-gray-200 p-4">
          <div className="text-[10px] tracking-widest uppercase text-gray-500 font-bold">Pending payout</div>
          <div className="font-display font-black text-2xl mt-1 text-[#B45309]">{fmtMoney(comTotals.pending)}</div>
        </div>
        <div className="border border-gray-200 p-4">
          <div className="text-[10px] tracking-widest uppercase text-gray-500 font-bold">Paid out</div>
          <div className="font-display font-black text-2xl mt-1 text-green-700">{fmtMoney(comTotals.paid_out)}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          { id: "pending", label: `Pending${pendingCount && tab !== "pending" ? ` (${pendingCount})` : ""}` },
          { id: "approved", label: "Approved" },
          { id: "all", label: "All reps" },
          { id: "commissions", label: "Commissions ledger" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${
              tab === t.id ? "border-[#0A1C40] text-[#0A1C40]" : "border-transparent text-gray-500 hover:text-black"
            }`}
            data-testid={`reps-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : tab === "commissions" ? (
        commissions.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-10 border border-dashed border-gray-300">
            No commissions yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200" data-testid="admin-commissions-table">
              <thead className="bg-gray-100 text-left">
                <tr>
                  <th className="p-2">Date</th>
                  <th className="p-2">Rep</th>
                  <th className="p-2">Order #</th>
                  <th className="p-2">Eligible items</th>
                  <th className="p-2 text-right">Eligible $</th>
                  <th className="p-2 text-right">Commission</th>
                  <th className="p-2">Status</th>
                  <th className="p-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((c) => (
                  <tr key={c.id} className="border-t align-top" data-testid={`commission-row-${c.id}`}>
                    <td className="p-2 whitespace-nowrap">{(c.created_at || "").slice(0, 10)}</td>
                    <td className="p-2">
                      <div className="font-bold">{c.rep_name}</div>
                      <div className="text-xs text-gray-500">{c.rep_email}</div>
                    </td>
                    <td className="p-2 font-mono text-xs">{c.order_number}</td>
                    <td className="p-2 text-xs text-gray-600 max-w-xs">
                      {(c.eligible_items || []).map((it) => `${it.quantity}x ${it.name}`).join(", ")}
                    </td>
                    <td className="p-2 text-right">{fmtMoney(c.eligible_subtotal)}</td>
                    <td className="p-2 text-right font-bold text-green-700">{fmtMoney(c.commission_amount)}</td>
                    <td className="p-2">
                      {c.paid_out ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 font-bold">PAID</span>
                      ) : (
                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 font-bold">PENDING</span>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      {!c.paid_out && (
                        <button
                          onClick={() => markPaid(c)}
                          className="text-xs bg-[#FFC220] text-[#0A1C40] font-bold px-2 py-1 hover:bg-[#FFD24D]"
                          data-testid={`commission-mark-paid-${c.id}`}
                        >
                          Mark paid
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : reps.length === 0 ? (
        <div className="text-sm text-gray-500 text-center py-10 border border-dashed border-gray-300">
          No reps in this view.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-gray-200" data-testid="admin-reps-table">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="p-2">Name</th>
                <th className="p-2">Email / Phone</th>
                <th className="p-2">Status</th>
                <th className="p-2">Code</th>
                <th className="p-2 text-right">Earned</th>
                <th className="p-2 text-right">Pending</th>
                <th className="p-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reps.map((r) => (
                <tr key={r.id} className="border-t align-top" data-testid={`rep-row-${r.id}`}>
                  <td className="p-2 font-bold">{r.name}</td>
                  <td className="p-2 text-xs">
                    <div>{r.email}</div>
                    {r.phone && <div className="text-gray-500">{r.phone}</div>}
                    {r.pitch && <div className="text-gray-500 italic mt-1 max-w-xs">"{r.pitch}"</div>}
                  </td>
                  <td className="p-2">
                    {r.rep_status === "pending" && (
                      <span className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 font-bold">
                        <Clock className="w-3 h-3" /> PENDING
                      </span>
                    )}
                    {r.rep_status === "approved" && (
                      <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 px-2 py-0.5 font-bold">
                        <BadgeCheck className="w-3 h-3" /> APPROVED
                      </span>
                    )}
                    {r.rep_status === "rejected" && (
                      <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 font-bold">
                        REJECTED
                      </span>
                    )}
                    {r.is_active === false && r.rep_status !== "rejected" && (
                      <span className="block mt-1 text-[10px] text-gray-500">deactivated</span>
                    )}
                  </td>
                  <td className="p-2 font-mono text-xs">
                    {r.referral_code || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="p-2 text-right">{fmtMoney(r.total_earned || 0)}</td>
                  <td className="p-2 text-right text-[#B45309]">{fmtMoney(r.total_pending || 0)}</td>
                  <td className="p-2 text-right space-x-1 whitespace-nowrap">
                    {r.rep_status === "pending" && (
                      <>
                        <button
                          onClick={() => approve(r)}
                          className="bg-green-600 text-white text-xs font-bold px-3 py-1 hover:bg-green-700 inline-flex items-center gap-1"
                          data-testid={`rep-approve-${r.id}`}
                        >
                          <Check className="w-3 h-3" /> Approve
                        </button>
                        <button
                          onClick={() => reject(r)}
                          className="bg-gray-200 text-gray-700 text-xs font-bold px-3 py-1 hover:bg-gray-300 inline-flex items-center gap-1"
                          data-testid={`rep-reject-${r.id}`}
                        >
                          <X className="w-3 h-3" /> Reject
                        </button>
                      </>
                    )}
                    {r.rep_status === "approved" && (
                      <button
                        onClick={() => toggleActive(r)}
                        className="text-xs px-3 py-1 border border-gray-300 hover:bg-gray-50"
                        data-testid={`rep-toggle-${r.id}`}
                      >
                        {r.is_active === false ? "Re-activate" : "Deactivate"}
                      </button>
                    )}
                    <button
                      onClick={() => remove(r)}
                      className="text-xs px-2 py-1 text-red-600 hover:bg-red-50"
                      data-testid={`rep-delete-${r.id}`}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-xs text-gray-500 mt-6 inline-flex items-center gap-2">
        <CircleDollarSign className="w-3 h-3" />
        Reps share this link → <code className="bg-gray-100 px-1">/?ref=THEIR_CODE</code>. Buyers
        get tracked for 30 days via cookie.
      </div>
    </div>
  );
}
